"use client";

import { useResponsive } from "@dafiti/closet-ui/hooks";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useLocale } from "next-intl";
import { useEnvContext } from "next-runtime-env";
import { usePathname } from "next/navigation";

import {
  getCatalogInfiniteProductsQueryOptions,
  getCatalogProductsQueryOptions,
} from "../api/catalog.query";
import { useCatalogBlockContext } from "../components/CatalogPage/CatalogBlockContext";
import { getCatalogHeaders } from "../server/catalogHeaders.helper";
import type { ActiveCatalogFilters } from "../types/filters.type";
import { buildSearchPathForBff } from "../types/searchTerm.helper";
import { useCatalogSettings } from "./useCatalogSettings.hook";
import { useFilterParams } from "./useFilterParams.hook";

export function useFiltersQuery() {
  const { NEXT_PUBLIC_BFF_API } = useEnvContext();
  const pathname = usePathname();
  const locale = useLocale();
  const catalogBlockContext = useCatalogBlockContext();
  const [settings] = useCatalogSettings();
  const [filterParams] = useFilterParams();
  const { isMobile } = useResponsive();
  const isMobileResolved = isMobile ?? false;

  const activeFilters = Object.fromEntries(
    Object.entries(filterParams).filter(([, v]) => v !== null),
  ) as ActiveCatalogFilters;

  const filtersArg =
    Object.keys(activeFilters).length > 0 ? activeFilters : undefined;
  const sharedPath = buildSearchPathForBff(
    catalogBlockContext.path || pathname,
  );
  const sharedHeaders = getCatalogHeaders();

  // Desktop: shares cache with usePaginatedCatalogResults — no extra request
  const {
    queryKey: paginatedKey,
    queryFn: paginatedFn,
    retry,
    staleTime,
  } = getCatalogProductsQueryOptions({
    apiOrigin: NEXT_PUBLIC_BFF_API!,
    path: sharedPath,
    isCMSPath: catalogBlockContext.isCMS,
    pagination: { page: settings.page },
    sort: settings.sort,
    extraHeaders: sharedHeaders,
    filters: filtersArg,
    locale,
  });

  const paginatedQuery = useQuery({
    queryKey: paginatedKey,
    queryFn: paginatedFn,
    retry,
    staleTime,
    enabled: !isMobileResolved,
  });

  // Mobile: shares cache with useInfiniteCatalogResults — no extra request
  const {
    queryKey: infiniteKey,
    queryFn: infiniteFn,
    retry: infiniteRetry,
    staleTime: infiniteStaleTime,
  } = getCatalogInfiniteProductsQueryOptions({
    apiOrigin: NEXT_PUBLIC_BFF_API!,
    path: sharedPath,
    isCMSPath: catalogBlockContext.isCMS,
    sort: settings.sort,
    extraHeaders: sharedHeaders,
    filters: filtersArg,
    locale,
  });

  const infiniteQuery = useInfiniteQuery({
    queryKey: infiniteKey,
    queryFn: infiniteFn,
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.page < lastPage.pagination.total_pages
        ? lastPage.pagination.page + 1
        : undefined,
    retry: infiniteRetry,
    staleTime: infiniteStaleTime,
    enabled: isMobileResolved,
  });

  const lastInfinitePage = infiniteQuery.data?.pages.at(-1);

  const facets = isMobileResolved
    ? (lastInfinitePage?.facets ?? [])
    : (paginatedQuery.data?.facets ?? []);

  const total = isMobileResolved
    ? (lastInfinitePage?.pagination.total ?? 0)
    : (paginatedQuery.data?.pagination.total ?? 0);

  const isLoading = isMobileResolved
    ? infiniteQuery.isLoading
    : paginatedQuery.isLoading;

  const isFilterActive = (field: string) =>
    facets.find((f) => f.field === field)?.is_selected ?? false;

  return {
    query: paginatedQuery,
    facets,
    total,
    isLoading,
    isFilterActive,
  };
}
